import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { EAssetType, ExportStep, SaveAction } from '~containers/EditorPageContainer/types';
import { createClipFromClip, overwriteClipFromClip } from '~services/clip';
import { IClientThumbnail } from '~services/clip/types';
import { Console } from '~services/console';
import { createClipFromEvent } from '~services/live';
import { getRecentlyCreatedService } from '~services/recentlyCreated';
import { createClipFromVod, overwriteClipFromVod } from '~services/vod';
import { uploadThumbnail } from '~src/store/channelManager/channelManager.utilities';
import { ExportActions } from '~src/store/editor/export/constants';
import { PublishActions } from '~src/store/editor/publish/constants';
import { getStreamState } from '~src/store/editor/stream/stream.selectors';
import { IEditorStreamState } from '~src/store/editor/stream/types';
import { PaginationActions } from '~src/store/pagination/constants';
import { IExportClipWithMeta } from '~src/store/timeline/clip/types';

import { clientClipFactory, isCreateClip } from './publish.utilities';

const RECENTLY_CREATED_PAGE_SIZE = 6;

const buildPublishClipRequest = async (clip: IExportClipWithMeta, streamState: IEditorStreamState, saveAction: SaveAction) => {
    const {
        components,
        meta: { title, thumbnail },
    } = clip;
    const { assetId, hlsStreamUrl, dashStreamUrl, assetType, streamUrls, streamingConfigId, cdnInfo } = streamState;

    let clientThumbnail: IClientThumbnail | undefined;

    try {
        // do not overwrite the thumbnail on clip updating
        if (saveAction === SaveAction.CREATE && thumbnail) {
            clientThumbnail = await uploadThumbnail(thumbnail);
        }

        const clientClip = clientClipFactory(
            title,
            components,
            clientThumbnail,
            hlsStreamUrl,
            dashStreamUrl,
            streamUrls,
            saveAction,
            assetType,
            streamingConfigId,
            cdnInfo
        );

        if (isCreateClip(saveAction, clientClip)) {
            if (assetType === EAssetType.LIVE) {
                return await createClipFromEvent(assetId, clientClip);
            } else if (assetType === EAssetType.CLIP) {
                return await createClipFromClip(assetId, clientClip);
            } else if (assetType === EAssetType.VOD) {
                return await createClipFromVod(assetId, clientClip);
            }
        } else {
            if (assetType === EAssetType.CLIP) {
                return await overwriteClipFromClip(assetId, clientClip);
            } else if (assetType === EAssetType.VOD) {
                return await overwriteClipFromVod(assetId, clientClip);
            }
        }
    } catch (error) {
        Console.warn("Processing 'Publish Clips'", error);
        return undefined;
    }
};

function* resetRecentClipsPagination(assetType: EAssetType, assetId: number) {
    const pageService = getRecentlyCreatedService(assetType, RECENTLY_CREATED_PAGE_SIZE, assetId);
    yield put({ type: PaginationActions.ADD_SAGA, payload: { stateKey: 'recent-clip', pageService } });
}

function* handlePublishClip(action) {
    const { exportClips, saveAction } = action.payload;

    // set export step loading
    yield put({ type: ExportActions.SET_STEP, payload: { step: ExportStep.LOADING } });

    // get stream meta
    const streamState: IEditorStreamState = yield select(getStreamState);

    // process publish via a 'loop' ¯\_(ツ)_/¯ backend short-come
    const requests = exportClips.map((exportClip) => {
        return call(buildPublishClipRequest, exportClip, streamState, saveAction);
    });

    const responses = yield all(requests);

    // used just to count the number of errored exports
    const errors: undefined[] = responses.filter((response) => response === undefined);

    yield put({ type: ExportActions.SET_ERROR_COUNT, payload: { errorCount: errors.length, totalClips: requests.length } });

    // complete
    yield put({ type: ExportActions.SET_STEP, payload: { step: ExportStep.COMPLETE } });

    yield resetRecentClipsPagination(streamState.assetType, streamState.assetId);
}

export const publishSagas = [takeEvery(PublishActions.PUBLISH_CLIP_SAGA, handlePublishClip)];
